import React, { useEffect, useRef, useState } from "react";
import { Card, Empty, Typography, Space, Spin, Carousel, AutoComplete, Input, Row, Col, Descriptions, Timeline, Button, Tag, Tabs } from "antd";
import { SearchOutlined, InfoCircleOutlined, HistoryOutlined, DatabaseOutlined, ArrowRightOutlined } from "@ant-design/icons";
import DigiTwinLogo from "./DigiTwinLogo.js";
import useIsMobile from "../../../hooks/useIsMobile.js";

const { Title, Text } = Typography;

// Parses "# Heading\nlines…" md sections in the browser.
function parseMd(text) {
  const sections = [];
  let current = null;
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (line.startsWith("# ")) {
      if (current) sections.push(current);
      current = { heading: line.slice(2).trim(), lines: [] };
    } else if (current && line) {
      current.lines.push(line);
    }
  }
  if (current) sections.push(current);
  return sections;
}

const stripBullet = (line) => (line.startsWith("- ") ? line.slice(2) : line);

// Renders one md section's content as a Descriptions value — plain text for a single line,
// a small bullet list when a section has several (keeps this generic for any future section
// shape, instead of assuming every section is a flat key/value pair).
function SectionValue({ section }) {
  if (section.lines.length <= 1) {
    return <Text style={{ color: "rgba(255,255,255,0.85)" }}>{stripBullet(section.lines[0] || "—")}</Text>;
  }
  return (
    <Space direction="vertical" size={4}>
      {section.lines.map((line, i) => (
        <Text key={i} style={{ color: "rgba(255,255,255,0.85)" }}>· {stripBullet(line)}</Text>
      ))}
    </Space>
  );
}

const CARD_STYLE = { height: "100%", display: "flex", flexDirection: "column" };
const CARD_BODY_STYLES = { body: { flex: 1, overflowY: "auto", padding: "18px 22px" }, header: { flexShrink: 0 } };

function CardTitle({ icon, children }) {
  return (
    <Space size={8}>
      {icon}
      <span>{children}</span>
    </Space>
  );
}

const SLIDE_HEIGHT = 240;
// Source project images are 1230x410 (3:1). A fixed pixel height on mobile crops the image
// by a different amount at every phone width, so match the box to the image's own ratio
// instead — that way objectFit:cover never has to crop anything.
const SLIDE_ASPECT_RATIO = "1230 / 410";

function ProjectSlide({ project, isMobile }) {
  return (
    <div>
      <div
        style={{
          ...(isMobile ? { aspectRatio: SLIDE_ASPECT_RATIO } : { height: SLIDE_HEIGHT }),
          borderRadius: 12,
          overflow: "hidden",
          position: "relative",
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        {project.hasImage ? (
          <img
            src={`/api/projects/${project.id}/image?v=${project.imageVersion}`}
            alt={project.name}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <DigiTwinLogo size={56} color="rgba(255,255,255,0.12)" />
          </div>
        )}

        {/* Gradient overlay + project name/meta */}
        <div
          style={{
            position: "absolute",
            left: 0, right: 0, bottom: 0,
            padding: isMobile ? "28px 16px 12px" : "40px 24px 18px",
            background: "linear-gradient(180deg, transparent 0%, rgba(10,13,20,0.85) 80%)",
          }}
        >
          <Text strong style={{ fontSize: isMobile ? 16 : 20, letterSpacing: "-0.3px", display: "block", color: "#fff" }}>
            {project.name}
          </Text>
          <Text style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.55)" }}>
            {project.fileCount} model{project.fileCount !== 1 ? "s" : ""}
          </Text>
        </div>
      </div>
    </div>
  );
}

export default function ProjectSelector({ onFileSelect }) {
  const isMobile = useIsMobile();
  const [projects, setProjects]               = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState(null);
  const [selectedIndex, setSelectedIndex]     = useState(0);
  const [searchValue, setSearchValue]         = useState("");
  // id → [{ heading, lines }]
  const [projectSections, setProjectSections] = useState({});

  const carouselRef = useRef(null);

  useEffect(() => {
    fetch("/api/projects")
      .then((r) => r.json())
      .then((data) => {
        const list = data.projects || [];
        setProjects(list);
        setLoading(false);

        // Prefer sections already parsed server-side; only fall back to the CRA static
        // /project-info/*.md copy (kept for backward compat) when the API found none.
        list.forEach((p) => {
          if (p.sections && p.sections.length > 0) {
            setProjectSections((prev) => ({ ...prev, [p.id]: p.sections }));
            return;
          }
          fetch(`/project-info/${p.name}.md`)
            .then((r) => (r.ok ? r.text() : ""))
            .then((text) => {
              const sections = text ? parseMd(text) : [];
              setProjectSections((prev) => ({ ...prev, [p.id]: sections }));
            })
            .catch(() => {});
        });
      })
      .catch(() => {
        setError("Cannot connect to server. Is it running?");
        setLoading(false);
      });
  }, []);

  const selectedProject = projects[selectedIndex] || null;

  const openFile = (file) => {
    if (!selectedProject || !file) return;
    onFileSelect({
      projectId:   selectedProject.id,
      projectName: selectedProject.name,
      fileName:    file.name,
    });
  };

  const goToProject = (id) => {
    const idx = projects.findIndex((p) => p.id === id);
    if (idx < 0) return;
    setSelectedIndex(idx);
    carouselRef.current?.goTo(idx);
  };

  const searchOptions = searchValue
    ? projects
        .filter((p) => p.name.toLowerCase().includes(searchValue.toLowerCase()))
        .map((p) => ({ value: p.name, id: p.id }))
    : [];

  const sections    = selectedProject ? (projectSections[selectedProject.id] || []) : [];
  const updateLines = selectedProject
    ? (selectedProject.updates || []).flatMap((s) => s.lines)
    : [];

  // Info/Updates/Models panel bodies — built once and reused by both the desktop 3-column
  // Row (span={8} each, ~110px wide on a phone — unusably compacted) and the mobile Tabs
  // layout below (one panel full-width at a time), so there's a single source of truth.
  const infoContent = sections.length > 0 ? (
    <Descriptions
      column={1}
      size="small"
      items={sections.map((section) => ({
        key: section.heading,
        label: section.heading,
        children: <SectionValue section={section} />,
      }))}
    />
  ) : (
    <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<Text type="secondary">No project information available</Text>} />
    </div>
  );

  const updatesContent = updateLines.length > 0 ? (
    <Timeline
      items={updateLines.map((line, i) => ({
        key: i,
        color: "#4f9cf9",
        content: <Text style={{ fontSize: 13, color: "rgba(255,255,255,0.82)", lineHeight: 1.5 }}>{stripBullet(line)}</Text>,
      }))}
    />
  ) : (
    <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<Text type="secondary">No updates yet</Text>} />
    </div>
  );

  const modelsContent = selectedProject && selectedProject.files.length > 0 ? (
    <>
      <style>{`
        .ds-model-row { transition: background 120ms ease; }
        .ds-model-row:hover { background: rgba(79,156,249,0.06); }
        .ds-model-row:hover .ds-model-open { opacity: 1; }
        .ds-model-open { opacity: 0; transition: opacity 120ms ease; }
      `}</style>
      {/* Natural (numeric-aware) order: 0+000-20+001 < 20+001-40+001 < … < 100+001-110+198.60.
          /api/projects (frozen) returns raw readdir order — alphabetical on Windows, unspecified on Linux. */}
      {[...selectedProject.files]
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
        .map((f, i) => (
        <div
          key={f.name}
          className="ds-model-row"
          onClick={() => openFile(f)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            padding: "14px 22px",
            cursor: "pointer",
            borderTop: i === 0 ? "none" : "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <DatabaseOutlined style={{ fontSize: 18, color: "rgba(79,156,249,0.7)", flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text style={{ fontSize: 13, fontWeight: 600, display: "block" }} ellipsis>
              {f.name.replace(/\.bim$/i, "")}
            </Text>
            <Space size={6} style={{ marginTop: 3 }}>
              <Tag style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, margin: 0, lineHeight: "16px" }}>{f.sizeMB} MB</Tag>
              {f.modified && (
                <Text style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.3)" }}>
                  {new Date(f.modified).toLocaleDateString()}
                </Text>
              )}
            </Space>
          </div>
          <Button
            className="ds-model-open"
            type="text"
            size="small"
            shape="circle"
            icon={<ArrowRightOutlined />}
            onClick={(e) => { e.stopPropagation(); openFile(f); }}
          />
        </div>
      ))}
    </>
  ) : (
    <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Empty description="No models found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>

      {/* ── Header ── */}
      <div style={{ padding: isMobile ? "20px 16px 0" : "36px 48px 0", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 24, marginBottom: 28 }}>
          <Space size={10} align="center">
            <DigiTwinLogo size={28} color="#4f9cf9" />
            <Title level={2} style={{ margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, letterSpacing: "-0.5px" }}>
              Digi<em style={{ fontStyle: "normal", color: "#4f9cf9" }}>Twin</em>
            </Title>
          </Space>
        </div>
        <div style={{ height: 1, background: "linear-gradient(90deg, rgba(46,53,64,1) 0%, transparent 70%)" }} />
      </div>

      {loading && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: 48 }}>
          <Spin size="large" />
          <Text type="secondary">Scanning project folders…</Text>
        </div>
      )}
      {error && (
        <div style={{ textAlign: "center", padding: 32 }}>
          <Text type="danger">{error}</Text>
          <br /><br />
          <Text type="secondary" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11 }}>
            Make sure the server is running on port 3001
          </Text>
        </div>
      )}
      {!loading && !error && projects.length === 0 && (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Empty description="No projects found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      )}

      {!loading && !error && projects.length > 0 && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", padding: isMobile ? "16px 16px 16px" : "24px 48px 28px", minHeight: 0 }}>

          {/* ── Carousel — project picker ── */}
          <div style={{ flexShrink: 0, maxWidth: 720, margin: "0 auto", width: "100%" }}>
            <style>{`
              /* No circular chip — a soft dark shade along the edge instead, so the arrow
                 has contrast on any image without looking like a floating button. */
              .ds-carousel .slick-prev, .ds-carousel .slick-next {
                top: 0;
                height: 100%;
                width: 64px;
                margin: 0;
                transform: none;
                transition: background 150ms ease;
                z-index: 2;
              }
              .ds-carousel .slick-prev {
                left: 0;
                background: linear-gradient(to right, rgba(0,0,0,0.4), transparent);
              }
              .ds-carousel .slick-next {
                right: 0;
                background: linear-gradient(to left, rgba(0,0,0,0.4), transparent);
              }
              .ds-carousel .slick-prev:hover {
                background: linear-gradient(to right, rgba(0,0,0,0.6), transparent);
              }
              .ds-carousel .slick-next:hover {
                background: linear-gradient(to left, rgba(0,0,0,0.6), transparent);
              }
              /* The chevron glyph (::after) is positioned assuming the small default box
                 height/width — now that the box is full-height and wider, recenter it
                 vertically and give both sides an equal, explicit inset from the true image
                 edge (the default only sets an inset-from-start, which left the "next" glyph
                 sitting near its own box's left edge instead of mirrored near the right). */
              .ds-carousel .slick-prev::after, .ds-carousel .slick-next::after {
                top: 50%;
              }
              .ds-carousel .slick-prev::after {
                left: 22px;
                right: auto;
                transform: translateY(-50%) rotate(-45deg);
              }
              .ds-carousel .slick-next::after {
                right: 22px;
                left: auto;
                transform: translateY(-50%) rotate(135deg);
              }
              /* Dots (the "navigator" rectangles) — antd's default relies on a theme token
                 that isn't guaranteed contrast against an arbitrary photo, so give the row its
                 own dark pill background (same treatment as the arrows) and force each dot's
                 own color explicitly instead of trusting the token. Level with the name/model-
                 count text's own bottom padding, but horizontally centered — the name text is
                 short and left-anchored, so a centered pill doesn't collide with it. */
              .ds-carousel .slick-dots.slick-dots-bottom {
                bottom: 9px;
                left: 50%;
                right: auto;
                width: auto;
                transform: translateX(-50%);
                background: rgba(10,13,20,0.55);
                padding: 6px 14px;
                border-radius: 999px;
                backdrop-filter: blur(2px);
              }
              .ds-carousel .slick-dots li button {
                background: #fff !important;
                opacity: 0.4;
              }
              .ds-carousel .slick-dots li:hover button {
                opacity: 0.75;
              }
              .ds-carousel .slick-dots li.slick-active button {
                opacity: 1;
              }
            `}</style>
            <Carousel className="ds-carousel" ref={carouselRef} afterChange={setSelectedIndex} dots={projects.length > 1} arrows={projects.length > 1}>
              {projects.map((project) => (
                <ProjectSlide key={project.id} project={project} isMobile={isMobile} />
              ))}
            </Carousel>
          </div>

          {/* ── Search — jump the carousel to a project by name ── */}
          <div style={{ flexShrink: 0, maxWidth: 360, margin: "16px auto 0", width: "100%" }}>
            <AutoComplete
              value={searchValue}
              options={searchOptions}
              onChange={setSearchValue}
              onSelect={(value, option) => { goToProject(option.id); setSearchValue(""); }}
              style={{ width: "100%" }}
            >
              <Input placeholder="Search projects…" prefix={<SearchOutlined style={{ color: "rgba(255,255,255,0.3)" }} />} allowClear />
            </AutoComplete>
          </div>

          {/* ── Project Information / Updates / Models ── three even columns on desktop;
              on mobile a 110px-wide column is unusably compacted, so Tabs instead — one
              panel full-width at a time, Models active by default since picking a file is
              the actual next action on this page. */}
          {isMobile ? (
            <>
              <style>{`
                .ds-mobile-tabs { flex: 1; min-height: 0; min-width: 0; display: flex; flex-direction: column; overflow-x: hidden; }
                .ds-mobile-tabs .ant-tabs-content-holder { flex: 1; min-height: 0; overflow-x: hidden; }
                .ds-mobile-tabs .ant-tabs-content { height: 100%; }
                .ds-mobile-tabs .ant-tabs-tabpane { height: 100%; overflow-x: hidden; }
                .ds-mobile-tabpane { height: 100%; overflow-y: auto; overflow-x: hidden; }
              `}</style>
              <Tabs
                className="ds-mobile-tabs"
                defaultActiveKey="models"
                style={{ marginTop: 20 }}
                items={[
                  {
                    key: "models",
                    label: <CardTitle icon={<DatabaseOutlined style={{ color: "#4f9cf9" }} />}>Models</CardTitle>,
                    children: <div className="ds-mobile-tabpane">{modelsContent}</div>,
                  },
                  {
                    key: "info",
                    label: <CardTitle icon={<InfoCircleOutlined style={{ color: "#4f9cf9" }} />}>Info</CardTitle>,
                    children: <div className="ds-mobile-tabpane" style={{ padding: "18px 22px" }}>{infoContent}</div>,
                  },
                  {
                    key: "updates",
                    label: <CardTitle icon={<HistoryOutlined style={{ color: "#4f9cf9" }} />}>Updates</CardTitle>,
                    children: <div className="ds-mobile-tabpane" style={{ padding: "18px 22px" }}>{updatesContent}</div>,
                  },
                ]}
              />
            </>
          ) : (
            // Models is the only panel here you actually act on — Project Information and
            // Updates are both read-only detail about whichever project the carousel has
            // selected. A strict 3-even-column split gave all three the same visual weight,
            // so "where do I click to open a model" became an eye search. This is really a
            // master-detail screen (Models = the master list; Info/Updates = detail about the
            // current selection) — master-detail conventions favor letting the list dominate
            // on a wide screen, not splitting evenly with its own detail content. Models is
            // now wider, first in left-to-right reading order, and has a soft accent border
            // marking it as the interactive one (a plain static border, not the Welcome page's
            // animated BorderBeam — that's reserved for one true flagship card elsewhere).
            <Row gutter={20} style={{ flex: 1, marginTop: 24, overflow: "hidden", minHeight: 0 }}>

              <Col span={15} style={{ height: "100%" }}>
                <Card
                  title={<CardTitle icon={<DatabaseOutlined style={{ color: "#4f9cf9" }} />}>Models</CardTitle>}
                  style={{ ...CARD_STYLE, border: "1px solid rgba(79,156,249,0.35)" }}
                  styles={{ ...CARD_BODY_STYLES, body: { ...CARD_BODY_STYLES.body, padding: 0 } }}
                >
                  {modelsContent}
                </Card>
              </Col>

              <Col span={9} style={{ height: "100%" }}>
                <Card style={CARD_STYLE} styles={{ body: { flex: 1, overflowY: "auto", padding: 0, display: "flex", flexDirection: "column" } }}>
                  <div style={{ padding: "18px 22px 14px" }}>
                    <Text strong style={{ fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
                      <InfoCircleOutlined style={{ color: "#4f9cf9" }} /> Project Information
                    </Text>
                    <div style={{ marginTop: 14 }}>{infoContent}</div>
                  </div>
                  <div style={{ height: 1, background: "rgba(255,255,255,0.08)", margin: "4px 22px" }} />
                  <div style={{ padding: "14px 22px 18px" }}>
                    <Text strong style={{ fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
                      <HistoryOutlined style={{ color: "#4f9cf9" }} /> Updates
                    </Text>
                    <div style={{ marginTop: 14 }}>{updatesContent}</div>
                  </div>
                </Card>
              </Col>

            </Row>
          )}
        </div>
      )}
    </div>
  );
}
